import { useState, type ComponentProps } from "react";

import { cn } from "../lib/cn";
import { Icon } from "./Icon";

/** Shared look of text fields (Input, NumberInput, PatternInput). */
export const fieldBase =
  "w-full rounded-input border border-input bg-surface px-3.5 text-sm text-foreground " +
  "placeholder:text-muted-foreground transition-colors outline-none " +
  "focus-visible:border-foreground focus-visible:ring-2 focus-visible:ring-brand " +
  "disabled:cursor-not-allowed disabled:opacity-60 " +
  "aria-invalid:border-danger aria-invalid:ring-danger/20";

export interface InputProps extends ComponentProps<"input"> {
  /** Optional icon shown inside the field on the left. */
  startIcon?: React.ReactNode;
}

export function Input({ className, startIcon, ...props }: InputProps) {
  if (!startIcon) {
    return <input className={cn(fieldBase, "h-11", className)} {...props} />;
  }
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
        {startIcon}
      </span>
      <input className={cn(fieldBase, "h-11 pl-10", className)} {...props} />
    </div>
  );
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return <textarea className={cn(fieldBase, "min-h-24 py-2.5", className)} {...props} />;
}

export interface PasswordInputProps extends Omit<ComponentProps<"input">, "type"> {
  showLabel?: string;
  hideLabel?: string;
}

/** Password field with a show/hide toggle. */
export function PasswordInput({
  className,
  showLabel = "Show password",
  hideLabel = "Hide password",
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        type={visible ? "text" : "password"}
        className={cn(fieldBase, "h-11 pr-11", className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-1 my-auto flex size-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
      >
        <Icon name={visible ? "eye-off" : "eye"} />
      </button>
    </div>
  );
}
